import { useEffect, useMemo, useRef, useState } from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { getAuthorizedUsers, type AuthorizedUser } from '@/services/authorizedUsers'
import {
  deletePermissionOverride,
  updatePermissionOverrides,
  type PermissionOverride,
  type PermissionsResponse,
} from '@/services/permissions'
import { toast } from '@/store/toastStore'
import { useAuth } from '@/contexts/AuthContext'
import { groupPermissionPagesByNav } from '@/config/navPages'
import {
  ACCESS_OPTIONS,
  isSuperAdminEmail,
  type PageAccess,
} from '@/utils/pagePermissions'

type DraftOverride = {
  id: string
  userId: number | null
  pageKey: string | null
  access: PageAccess
}

function capitalizeRole(role: string): string {
  return role.charAt(0).toUpperCase() + role.slice(1)
}

function userOptionLabel(user: { email: string; role: string }): string {
  return `${user.email} (${capitalizeRole(user.role)})`
}

type UserPermissionOverridesProps = {
  data: PermissionsResponse
  onDataChange: (response: PermissionsResponse) => void
}

export default function UserPermissionOverrides({ data, onDataChange }: UserPermissionOverridesProps) {
  const { user, refresh } = useAuth()
  const [authorizedUsers, setAuthorizedUsers] = useState<AuthorizedUser[]>([])
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [drafts, setDrafts] = useState<DraftOverride[]>([])
  const [savingRow, setSavingRow] = useState<string | null>(null)
  const nextDraftId = useRef(0)

  const pageGroups = useMemo(() => groupPermissionPagesByNav(data.pages), [data.pages])

  const eligibleUsers = useMemo(
    () =>
      authorizedUsers.filter(
        (authorizedUser) =>
          authorizedUser.is_active && !isSuperAdminEmail(authorizedUser.email),
      ),
    [authorizedUsers],
  )

  const userById = useMemo(() => {
    const map = new Map<number, AuthorizedUser>()
    for (const authorizedUser of authorizedUsers) {
      map.set(authorizedUser.id, authorizedUser)
    }
    return map
  }, [authorizedUsers])

  useEffect(() => {
    const loadUsers = async () => {
      setLoadingUsers(true)
      try {
        const response = await getAuthorizedUsers()
        setAuthorizedUsers(response.users)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load authorized users')
      } finally {
        setLoadingUsers(false)
      }
    }
    void loadUsers()
  }, [])

  const refreshAuthIfNeeded = (updatedEmails: string[]) => {
    if (user && updatedEmails.includes(user.email)) {
      void refresh()
    }
  }

  const isDuplicatePair = (
    userId: number,
    pageKey: string,
    exclude?: { draftId?: string; savedUserId?: number; savedPageKey?: string },
  ): boolean => {
    const inSaved = data.overrides.some(
      (override) =>
        override.user_id === userId &&
        override.page_key === pageKey &&
        !(
          exclude?.savedUserId === override.user_id &&
          exclude?.savedPageKey === override.page_key
        ),
    )
    const inDrafts = drafts.some(
      (draft) =>
        draft.userId === userId &&
        draft.pageKey === pageKey &&
        draft.id !== exclude?.draftId,
    )
    return inSaved || inDrafts
  }

  const handleAddOverride = () => {
    nextDraftId.current += 1
    setDrafts((prev) => [
      {
        id: `draft-${nextDraftId.current}`,
        userId: null,
        pageKey: null,
        access: 'edit',
      },
      ...prev,
    ])
  }

  const persistDraft = async (draft: DraftOverride) => {
    if (!draft.userId || !draft.pageKey) return

    setSavingRow(draft.id)
    try {
      const response = await updatePermissionOverrides([
        { user_id: draft.userId, page_key: draft.pageKey, access: draft.access },
      ])
      setDrafts((prev) => prev.filter((item) => item.id !== draft.id))
      onDataChange(response)
      const email = userById.get(draft.userId)?.email
      if (email) {
        refreshAuthIfNeeded([email])
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save override')
    } finally {
      setSavingRow(null)
    }
  }

  const handleDraftChange = (draftId: string, updates: Partial<DraftOverride>) => {
    const draft = drafts.find((item) => item.id === draftId)
    if (!draft) return

    const updated: DraftOverride = { ...draft, ...updates }
    const nextUserId = updated.userId
    const nextPageKey = updated.pageKey

    if (nextUserId && nextPageKey) {
      if (isDuplicatePair(nextUserId, nextPageKey, { draftId })) {
        toast.error('This user already has an override for this page.')
        return
      }
    }

    setDrafts((prev) => prev.map((item) => (item.id === draftId ? updated : item)))

    if (nextUserId && nextPageKey) {
      void persistDraft(updated)
    }
  }

  const handleAccessChange = async (override: PermissionOverride, access: PageAccess) => {
    const rowKey = `${override.user_id}:${override.page_key}`
    setSavingRow(rowKey)
    try {
      const response = await updatePermissionOverrides([
        { user_id: override.user_id, page_key: override.page_key, access },
      ])
      onDataChange(response)
      refreshAuthIfNeeded([override.email])
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update override')
    } finally {
      setSavingRow(null)
    }
  }

  const handleSavedPairChange = async (
    override: PermissionOverride,
    nextUserId: number,
    nextPageKey: string,
  ) => {
    if (nextUserId === override.user_id && nextPageKey === override.page_key) return

    if (isDuplicatePair(nextUserId, nextPageKey, {
      savedUserId: override.user_id,
      savedPageKey: override.page_key,
    })) {
      toast.error('This user already has an override for this page.')
      return
    }

    const rowKey = `${override.user_id}:${override.page_key}`
    setSavingRow(rowKey)
    try {
      const putResponse = await updatePermissionOverrides([
        {
          user_id: nextUserId,
          page_key: nextPageKey,
          access: override.access,
        },
      ])
      onDataChange(putResponse)

      const nextEmail = userById.get(nextUserId)?.email
      try {
        const deleteResponse = await deletePermissionOverride(
          override.user_id,
          override.page_key,
        )
        onDataChange(deleteResponse)
        refreshAuthIfNeeded([override.email, ...(nextEmail ? [nextEmail] : [])])
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update override')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update override')
    } finally {
      setSavingRow(null)
    }
  }

  const handleDeleteDraft = (draftId: string) => {
    setDrafts((prev) => prev.filter((item) => item.id !== draftId))
  }

  const handleDeleteSaved = async (override: PermissionOverride) => {
    const rowKey = `${override.user_id}:${override.page_key}`
    setSavingRow(rowKey)
    try {
      const response = await deletePermissionOverride(override.user_id, override.page_key)
      onDataChange(response)
      refreshAuthIfNeeded([override.email])
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete override')
    } finally {
      setSavingRow(null)
    }
  }

  const savedUserOptions = (override: PermissionOverride) => {
    const options = [...eligibleUsers]
    if (!options.some((item) => item.id === override.user_id)) {
      options.unshift({
        id: override.user_id,
        email: override.email,
        is_active: true,
        role: override.role,
      })
    }
    return options
  }

  const hasRows = drafts.length > 0 || data.overrides.length > 0

  return (
    <div className="mt-8 border-t border-gray-100 pt-8">
      <h3 className="container-title">User overrides</h3>
      <p className="mt-1 text-sm text-gray-500">
        Give specific people different access than their role on selected pages. Everyone else keeps
        the role defaults above.
      </p>

      <div className="mt-4">
        <button
          type="button"
          onClick={handleAddOverride}
          className="btn btn-primary text-sm"
        >
          <PlusIcon className="h-4 w-4" />
          Add override
        </button>
      </div>

      <div className="mt-6">
        {!hasRows ? (
          <p className="text-sm text-gray-500">
            No user overrides yet. Role permissions apply to everyone.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
            {drafts.map((draft) => {
              const isSaving = savingRow === draft.id
              return (
                <li
                  key={draft.id}
                  className="flex flex-col gap-2 px-4 py-3 text-sm sm:flex-row sm:items-center sm:gap-3"
                >
                  <select
                    className="input select min-w-0 flex-1 text-sm"
                    value={draft.userId ?? ''}
                    disabled={isSaving || loadingUsers}
                    onChange={(e) =>
                      handleDraftChange(draft.id, {
                        userId: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                  >
                    <option value="">Select user…</option>
                    {eligibleUsers.map((authorizedUser) => (
                      <option key={authorizedUser.id} value={authorizedUser.id}>
                        {userOptionLabel(authorizedUser)}
                      </option>
                    ))}
                  </select>

                  <select
                    className="input select min-w-0 flex-1 text-sm"
                    value={draft.pageKey ?? ''}
                    disabled={isSaving}
                    onChange={(e) =>
                      handleDraftChange(draft.id, {
                        pageKey: e.target.value || null,
                      })
                    }
                  >
                    <option value="">Select page…</option>
                    {pageGroups.map((group) => (
                      <optgroup key={`draft-section-${group.section}`} label={group.section}>
                        {group.pages.map((page) => (
                          <option key={page.key} value={page.key}>
                            {page.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>

                  <select
                    className="input select !w-[7.5rem] shrink-0 text-sm"
                    value={draft.access}
                    disabled={isSaving}
                    onChange={(e) =>
                      handleDraftChange(draft.id, {
                        access: e.target.value as PageAccess,
                      })
                    }
                  >
                    {ACCESS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => handleDeleteDraft(draft.id)}
                    disabled={isSaving}
                    className="btn btn-secondary shrink-0 text-sm"
                  >
                    Delete
                  </button>
                </li>
              )
            })}

            {data.overrides.map((override) => {
              const rowKey = `${override.user_id}:${override.page_key}`
              const isSaving = savingRow === rowKey
              return (
                <li
                  key={rowKey}
                  className="flex flex-col gap-2 px-4 py-3 text-sm sm:flex-row sm:items-center sm:gap-3"
                >
                  <select
                    className="input select min-w-0 flex-1 text-sm"
                    value={override.user_id}
                    disabled={isSaving || loadingUsers}
                    onChange={(e) =>
                      void handleSavedPairChange(
                        override,
                        Number(e.target.value),
                        override.page_key,
                      )
                    }
                  >
                    {savedUserOptions(override).map((authorizedUser) => (
                      <option key={authorizedUser.id} value={authorizedUser.id}>
                        {userOptionLabel(authorizedUser)}
                      </option>
                    ))}
                  </select>

                  <select
                    className="input select min-w-0 flex-1 text-sm"
                    value={override.page_key}
                    disabled={isSaving}
                    onChange={(e) =>
                      void handleSavedPairChange(
                        override,
                        override.user_id,
                        e.target.value,
                      )
                    }
                  >
                    {pageGroups.map((group) => (
                      <optgroup key={`saved-section-${group.section}-${rowKey}`} label={group.section}>
                        {group.pages.map((page) => (
                          <option key={page.key} value={page.key}>
                            {page.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>

                  <select
                    className="input select !w-[7.5rem] shrink-0 text-sm"
                    value={override.access}
                    disabled={isSaving}
                    onChange={(e) =>
                      void handleAccessChange(override, e.target.value as PageAccess)
                    }
                  >
                    {ACCESS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => void handleDeleteSaved(override)}
                    disabled={isSaving}
                    className="btn btn-secondary shrink-0 text-sm"
                  >
                    Delete
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
